import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Textarea from "../../../components/ui/Textarea";
import { Checkbox } from "../../../components/ui/Checkbox";
import { getAreaByGovService } from "../../../redux/services/general_service";
import ProductImageUpload from "./ProductImageUpload";

import {
  GoogleMap,
  Marker,
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";

const WorkshopFormModal = ({
  isOpen,
  onClose,
  onSave,
  workshop,
  mode,
  language,
}) => {
  const dispatch = useDispatch();
  const { governoratesList, areas } = useSelector((state) => state.general);
  const { tagsList, isLoadingAction } = useSelector(
    (state) => state.workshops,
  );

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyDFeaKGPKpoglLfONtavQKnRYxbrn2ckkg",
    libraries: ["places"],
  });

  const [autocomplete, setAutocomplete] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    images: [],
    description: "",
    governorate_id: "",
    area_id: "",
    address: "",
    phone_number: "",
    is_emergency_provided: false,
    tags: [],
    latitude: "",
    longitude: "",
  });

  const [errors, setErrors] = useState({});
  const [position, setPosition] = useState(null);

  const labels = {
    addTitle: { ar: "إضافة ورشة", en: "Add Workshop" },
    editTitle: { ar: "تعديل ورشة", en: "Edit Workshop" },
    name: { ar: "اسم الورشة", en: "Workshop Name" },
    description: { ar: "الوصف", en: "Description" },
    governorate: { ar: "المحافظة", en: "Governorate" },
    area: { ar: "المنطقة", en: "Area" },
    address: { ar: "العنوان", en: "Address" },
    phone_number: { ar: "رقم الهاتف", en: "Phone Number" },
    emergency: { ar: "خدمة طوارئ", en: "Emergency Service" },
    tags: { ar: "الوسوم", en: "Tags" },
    images: { ar: "الصور", en: "Images" },
    location: { ar: "الموقع", en: "Location" },
    search_location: { ar: "بحث عن مكان", en: "Search location" },
    cancel: { ar: "إلغاء", en: "Cancel" },
    save: { ar: "حفظ", en: "Save" },
    required: { ar: "هذا الحقل مطلوب", en: "This field is required" },
  };

  // ===== Load areas =====
  useEffect(() => {
    if (formData.governorate_id) {
      dispatch(
        getAreaByGovService({
          governorate_id: formData.governorate_id,
        }),
      );
      setFormData((prev) => ({ ...prev, area_id: "" }));
    }
  }, [formData.governorate_id, dispatch]);

  // ===== Load workshop =====
  useEffect(() => {
    if (workshop) {
      const images = workshop.images
        ? (Array.isArray(workshop.images)
            ? workshop.images
            : [workshop.images]
          ).map((url, idx) => ({
            id: Date.now() + idx,
            file: null,
            url,
            isPrimary: idx === 0,
          }))
        : [];

      setFormData({
        name: workshop.name,
        description: workshop.description,
        governorate_id: workshop.governorate_id,
        area_id: workshop.area_id,
        address: workshop.address,
        phone_number: workshop.phone_number,
        is_emergency_provided:
          workshop.is_emergency_provided || false,
        tags: workshop.tags
          ? workshop.tags.map((t) => t.id)
          : [],
        images,
        latitude: workshop.latitude,
        longitude: workshop.longitude,
      });

      if (workshop.latitude && workshop.longitude) {
        setPosition({
          lat: parseFloat(workshop.latitude),
          lng: parseFloat(workshop.longitude),
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        images: [],
        tags: [],
      }));
      setPosition(null);
    }

    setErrors({});
  }, [workshop, isOpen]);

  // ===== Validation =====
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim())
      newErrors.name = labels.required[language];
    if (!formData.governorate_id)
      newErrors.governorate_id = labels.required[language];
    if (!formData.area_id)
      newErrors.area_id = labels.required[language];
    if (!formData.description.trim())
      newErrors.description = labels.required[language];
    if (!formData.tags.length)
      newErrors.tags = labels.required[language];

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ===== Submit =====
  const handleSubmit = (e) => {
    e.preventDefault();
   // if (!validateForm()) return;

    if (position) {
      formData.latitude = position.lat;
      formData.longitude = position.lng;
    }

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("description", formData.description);
    submitData.append("governorate_id", formData.governorate_id);
    submitData.append("area_id", formData.area_id);
    submitData.append("address", formData.address);
    submitData.append("phone_number", formData.phone_number);
    submitData.append(
      "is_emergency_provided",
      formData.is_emergency_provided ? 1 : 0,
    );
    submitData.append("latitude", formData.latitude);
    submitData.append("longitude", formData.longitude);

    formData.tags.forEach((tagId, index) =>
      submitData.append(`tags[${index}]`, tagId),
    );

    formData.images.forEach((img, index) => {
      if (img.file)
        submitData.append(`images[${index}]`, img.file);
    });

    onSave(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card w-full max-w-4xl rounded-xl border border-border max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between">
          <h2 className="text-xl font-semibold">
            {mode === "add"
              ? labels.addTitle[language]
              : labels.editTitle[language]}
          </h2>
          <button onClick={onClose}>
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          <Input
            label={labels.name[language]}
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            error={errors.name}
            required
          />

          <Textarea
            label={labels.description[language]}
            value={formData.description}
            onChange={(e) =>
              setFormData({
                ...formData,
                description: e.target.value,
              })
            }
            error={errors.description}
          />

          <Select
            label={labels.governorate[language]}
            options={governoratesList?.map((g) => ({
              value: g.id,
              label: g.name,
            }))}
            value={formData.governorate_id}
            onChange={(v) =>
              setFormData({
                ...formData,
                governorate_id: v,
                area_id: "",
              })
            }
          />

          <Select
            label={labels.area[language]}
            options={areas?.map((a) => ({
              value: a.id,
              label: a.area_name,
            }))}
            value={formData.area_id}
            onChange={(v) =>
              setFormData({ ...formData, area_id: v })
            }
          />

          <Input
            label={labels.address[language]}
            value={formData.address}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: e.target.value,
              })
            }
          />

          <Input
            label={labels.phone_number[language]}
            value={formData.phone_number}
            onChange={(e) =>
              setFormData({
                ...formData,
                phone_number: e.target.value,
              })
            }
          />

          {/* ✅ التاغات بدون أي تعديل */}
          <Select
            label={labels.tags[language]}
            options={tagsList?.map((t) => ({
              value: t.id,
              label: t.name,
            }))}
            value={formData.tags}
            onChange={(v) =>
              setFormData({ ...formData, tags: v })
            }
            multiple
            searchable
            error={errors.tags}
          />

          <ProductImageUpload
            images={formData.images}
            onChange={(images) =>
              setFormData({ ...formData, images })
            }
          />

          {/* MAP */}
          <div>
            <label className="font-medium">
              {labels.location[language]}
            </label>

            {/* 🔥 البحث */}
            <div className="mb-2">
              {isLoaded && (
                <Autocomplete
                  onLoad={(auto) => setAutocomplete(auto)}
                  onPlaceChanged={() => {
                    if (autocomplete) {
                      const place = autocomplete.getPlace();
                      if (!place.geometry) return;

                      const lat = place.geometry.location.lat();
                      const lng = place.geometry.location.lng();

                      setPosition({ lat, lng });
                      setSearchValue(place.formatted_address || "");
                    }
                  }}
                >
                  <Input
                    placeholder={labels.search_location[language]}
                    value={searchValue}
                    onChange={(e) =>
                      setSearchValue(e.target.value)
                    }
                  />
                </Autocomplete>
              )}
            </div>

            {/* MAP */}
            <div className="h-64 rounded-lg overflow-hidden border">
              {!isLoaded ? (
                <div>Loading...</div>
              ) : (
                <GoogleMap
                  center={
                    position || {
                      lat: 33.3152,
                      lng: 44.3661,
                    }
                  }
                  zoom={position ? 15 : 6}
                  mapContainerStyle={{
                    width: "100%",
                    height: "100%",
                  }}
                  onClick={(e) =>
                    setPosition({
                      lat: e.latLng.lat(),
                      lng: e.latLng.lng(),
                    })
                  }
                >
                  {position && (
                    <Marker position={position} />
                  )}
                </GoogleMap>
              )}
            </div>

            {/* 🔥 إدخال يدوي للإحداثيات */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Input
                label="Latitude"
                value={position?.lat || ""}
                onChange={(e) => {
                  const lat = parseFloat(e.target.value);
                  if (!isNaN(lat)) {
                    setPosition((prev) => ({
                      lat,
                      lng: prev?.lng || 0,
                    }));
                  }
                }}
              />

              <Input
                label="Longitude"
                value={position?.lng || ""}
                onChange={(e) => {
                  const lng = parseFloat(e.target.value);
                  if (!isNaN(lng)) {
                    setPosition((prev) => ({
                      lat: prev?.lat || 0,
                      lng,
                    }));
                  }
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              {labels.cancel[language]}
            </Button>

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoadingAction}
            >
              {labels.save[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkshopFormModal;